import React, { useState, useEffect } from 'react';
import Button from '../ui/button';
import Badge from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Play,
  Eye,
  Settings,
  Trash2,
  Copy,
  Download,
  MoreHorizontal,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
  Clipboard as ClipboardIcon
} from 'lucide-react';
import ModelComparisonResults from './ModelComparisonResults';
import modelComparisonService from '../../services/modelComparisonService';

const ModelComparisonCard = ({
  comparison,
  onEdit,
  onDelete,
  onExecute,
  onClone
}) => {
  const [showResults, setShowResults] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditHasMore, setAuditHasMore] = useState(false);

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'outline', icon: null, color: 'text-blue-600' },
      running: { variant: 'default', icon: Clock, color: 'text-blue-600' },
      completed: { variant: 'secondary', icon: CheckCircle, color: 'text-green-600' },
      failed: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      paused: { variant: 'outline', icon: Pause, color: 'text-yellow-600' }
    };

    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getModelNames = (models) => {
    if (!models || !Array.isArray(models)) return [];
    return models.map(model => `${model.provider}/${model.model}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canExecute = comparison.status === 'active' || comparison.status === 'paused';
  const isRunning = comparison.status === 'running';
  const hasResults = comparison.status === 'completed' && comparison.results;

  const handleCardClick = () => {
    if (hasResults) {
      setShowResults(true);
    } else {
      onEdit(comparison);
    }
  };

  // Load audit logs when modal opens and when page changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!showAudit) return;
      setAuditLoading(true);
      try {
        const resp = await modelComparisonService.getComparisonAuditLogs(comparison.id, { per_page: 50, page: auditPage });
        const payload = resp?.data;
        const items = Array.isArray(payload?.data) ? payload.data : [];
        const next = !!payload?.next_page_url;
        if (cancelled) return;
        setAuditLogs((prev) => (auditPage === 1 ? items : [...prev, ...items]));
        setAuditHasMore(next);
      } catch (e) {
        // ignore for UI
      } finally {
        if (!cancelled) setAuditLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showAudit, auditPage, comparison.id]);

  const actionStyles = (action) => {
    const a = String(action || '').toLowerCase();
    if (a.includes('failed')) return { dot: 'bg-red-500', badge: 'destructive' };
    if (a.includes('completed') || a.includes('passed')) return { dot: 'bg-green-500', badge: 'secondary' };
    if (a.includes('started') || a.includes('created') || a.includes('execution')) return { dot: 'bg-blue-500', badge: 'default' };
    return { dot: 'bg-gray-400', badge: 'outline' };
  };

  const copyMetadata = async (metadata) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(metadata ?? {}, null, 2));
    } catch (e) {
      // noop
    }
  };

  return (
    <>
      <Card onClick={handleCardClick} className="hover:bg-accent/50 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-medium">{comparison.name}</h3>
              {getStatusBadge(comparison.status)}
              {isRunning && (
                <span className="text-sm text-gray-500">
                  {comparison.progress || 0}% complete
                </span>
              )}
            </div>

            {/* Description */}
            {comparison.description && (
              <p className="text-sm text-gray-600 mb-3">
                {comparison.description}
              </p>
            )}

            {/* Models */}
            <div className="mb-3">
              <div className="flex flex-wrap gap-1">
                {getModelNames(comparison.models).map((modelName, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {modelName}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Metadata */}
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <span>Models: {comparison.models?.length || 0}</span>
              {comparison.template && (
                <span>Template: {comparison.template}</span>
              )}
              <span>Created: {formatDate(comparison.created_at)}</span>
              {comparison.last_run_at && (
                <span>Last run: {formatDate(comparison.last_run_at)}</span>
              )}
            </div>

            {/* Progress Bar */}
            {isRunning && (
              <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${comparison.progress || 0}%` }}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            {hasResults && (
              <Button
                variant="ghost"
                size="icon"
                  onClick={(e) => { e.stopPropagation(); setShowResults(true); }}
                title="View Results"
              >
                <Eye className="w-4 h-4" />
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
                onClick={(e) => { e.stopPropagation(); onExecute(comparison.id); }}
              disabled={!canExecute}
              title={canExecute ? "Execute Comparison" : "Cannot execute in current state"}
            >
              <Play className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
                onClick={(e) => { e.stopPropagation(); onEdit(comparison); }}
              title="Edit Comparison"
            >
              <Settings className="w-4 h-4" />
            </Button>

            {/* More Actions Dropdown */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                  onClick={(e) => { e.stopPropagation(); setShowActions(!showActions); }}
                title="More Actions"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>

              {showActions && (
                <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-10 min-w-[150px]">
                  <button
                      onClick={(e) => {
                        e.stopPropagation();
                      onClone(comparison.id, comparison.name);
                      setShowActions(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Clone
                  </button>

                  {hasResults && (
                    <button
                        onClick={(e) => {
                          e.stopPropagation();
                        // TODO: Implement export functionality
                        setShowActions(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export
                    </button>
                  )}

                  {/* Audit Logs */}
                  <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAuditPage(1);
                        setAuditLogs([]);
                        setAuditHasMore(false);
                        setShowAudit(true);
                        setShowActions(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Clock className="w-4 h-4" />
                      Audit Logs
                  </button>

                  <hr className="my-1" />

                  <button
                      onClick={(e) => {
                        e.stopPropagation();
                      onDelete(comparison.id, comparison.name);
                      setShowActions(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Modal */}
      {showResults && (
        <ModelComparisonResults
          comparisonId={comparison.id}
          comparisonName={comparison.name}
          onClose={() => setShowResults(false)}
        />
      )}

      {/* Audit Logs Modal */}
      {showAudit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAudit(false)}>
          <div className="bg-white border rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Audit Logs - {comparison.name}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAudit(false)}>Close</Button>
            </div>
            <div className="p-4">
              {auditLoading && auditPage === 1 && (
                <div className="text-sm text-gray-500">Loading…</div>
              )}
              {!auditLoading && auditLogs.length === 0 && (
                <div className="text-sm text-gray-500">No audit logs available</div>
              )}
              {auditLogs.length > 0 && (
                <>
                  <div className="relative pl-6">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-200" />
                    <div className="space-y-3">
                      {auditLogs.map((log) => {
                        const style = actionStyles(log.action);
                        const msg = log?.metadata?.message || log?.metadata?.error || '';
                        return (
                          <div key={log.id} className="relative">
                            <div className={`absolute -left-2 top-2 w-3 h-3 rounded-full border border-white ${style.dot}`} />
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <Badge variant={style.badge} className="mb-1">
                                  {log.action}
                                </Badge>
                                {msg && (
                                  <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                                    {msg}
                                  </div>
                                )}
                                {log?.metadata && !msg && (
                                  <pre className="mt-1 bg-gray-50 border rounded p-2 text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap break-words">
{JSON.stringify(log.metadata, null, 2)}
                                  </pre>
                                )}
                                {log?.metadata && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-1 text-xs"
                                    onClick={() => copyMetadata(log.metadata)}
                                  >
                                    <ClipboardIcon className="w-3 h-3 mr-1" /> Copy details
                                  </Button>
                                )}
                              </div>
                              <span className="shrink-0 text-xs text-gray-500">{formatDate(log.created_at)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-4">
                    {auditHasMore ? (
                      <Button size="sm" variant="outline" disabled={auditLoading} onClick={() => setAuditPage((p) => p + 1)}>
                        {auditLoading ? 'Loading…' : 'Load more'}
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-500">End of timeline</span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close actions */}
      {showActions && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowActions(false)}
        />
      )}
    </>
  );
};

export default ModelComparisonCard;
